import { Button, Checkbox, Flex, Input, InputNumber, Select } from 'antd'
import { useRolodexEdit } from 'features/fde'
import { useKestrel } from 'features/kestrelViewer'
import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { exportG1N5PDF } from 'features/kestrelViewer/utils/pdfExportUtils'
import React, { FC, useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { G1N5, RolodexEntryPatchJson } from 'types'

const { TextArea } = Input
const { Option } = Select

const fadeInModal = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-0.5rem);
  }
  to {
    opacity: 0.95;
    transform: scale(1) translateY(0);
  }
`

const ModalBackdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2500;
`

const ModalContainer = styled.div`
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1rem;
  color: white;
  line-height: 1rem;
  width: 37rem;
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;
  opacity: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  animation: ${fadeInModal} 0.3s ease-out forwards;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
`

const TwoColumnRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  width: 100%;
`

const LeftSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const RightSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const InputGroup = styled.div`
  margin-bottom: 1rem;
  text-align: left;
`

const InputHeader = styled.div`
  font-size: 0.65rem;
  opacity: 0.95;
  letter-spacing: 0.05rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
`

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
`

export const G1N5Form: FC = () => {
  const { kestrelModel, annotations } = useKestrel()
  const { setG1n5FormOpen, g1n5, setG1n5, photos } = useKestrelViewerContext()
  const { rolodexPatch, rolodexUploadFile } = useRolodexEdit()
  const [exporting, setExporting] = useState(false)

  const [editedG1N5, setEditedG1N5] = useState<G1N5>(() => {
    if (g1n5 && g1n5 !== undefined) {
      return JSON.parse(JSON.stringify(g1n5))
    } else {
      return new G1N5()
    }
  })

  useEffect(() => {
    setEditedG1N5(JSON.parse(JSON.stringify(g1n5)))
  }, [g1n5])

  const handleSave = async () => {
    if (!kestrelModel?.id) return

    // Update rolodex
    const patchData: RolodexEntryPatchJson = {
      id: kestrelModel.id,
      data: {
        g1n5: G1N5.toJSON(editedG1N5),
      },
    }
    await rolodexPatch([patchData])
    setG1n5(editedG1N5)
    setG1n5FormOpen(false)
  }

  const handleExport = async () => {
    if (exporting) return // Prevent multiple clicks

    setExporting(true)

    try {
      // Extract component metadata from kestrelModel tags
      const metadata = {
        organizationName: kestrelModel?.tags.organization,
        siteName: kestrelModel?.tags.site,
        componentName: kestrelModel?.tags.component,
      }

      // Export G1N5 data with annotations to PDF
      const pdfBlob = await exportG1N5PDF(
        editedG1N5,
        annotations,
        metadata,
        photos
      )

      // Upload the PDF directly to the existing kestrel-model entry
      const filename = 'g1n5.pdf'
      const filePath = `files/g1n5/${filename}`
      await rolodexUploadFile(kestrelModel?.id || '', pdfBlob, filePath)

      // Download the file to the browser
      const url = URL.createObjectURL(pdfBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to save G1N5 PDF', error)
    } finally {
      setExporting(false)
    }
  }

  const onClose = () => {
    setG1n5FormOpen(false)
  }

  const updateG1N5Data = <K extends keyof G1N5>(field: K, value: G1N5[K]) => {
    setEditedG1N5((prev: G1N5) => ({ ...prev, [field]: value }))
  }

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <TwoColumnRow>
          <LeftSection>
            <InputHeader>% Area Visible</InputHeader>
            <InputNumber
              min={0}
              max={100}
              value={editedG1N5.areaVisible}
              onChange={(value) => updateG1N5Data('areaVisible', value)}
              style={{ width: '100%' }}
              placeholder='0-100'
            />
          </LeftSection>
          <RightSection>
            <InputHeader>% Corrosion/Coating Failure</InputHeader>
            <InputNumber
              min={0}
              max={100}
              value={editedG1N5.corrosionCoatingFailure}
              onChange={(value) =>
                updateG1N5Data('corrosionCoatingFailure', value)
              }
              style={{ width: '100%' }}
              placeholder='0-100'
            />
          </RightSection>
        </TwoColumnRow>

        <TwoColumnRow>
          <LeftSection>
            <InputHeader>Total Area of Zone</InputHeader>
            <InputNumber
              min={0}
              value={editedG1N5.totalArea}
              onChange={(value) => updateG1N5Data('totalArea', value)}
              style={{ width: '100%' }}
              placeholder='Area'
            />
          </LeftSection>
          <RightSection>
            <InputHeader>Corrosion Type</InputHeader>
            <Select
              value={editedG1N5.corrosionType}
              onChange={(value) => updateG1N5Data('corrosionType', value)}
              style={{ width: '100%' }}
              placeholder='Select type'>
              <Option value='local'>Local</Option>
              <Option value='scattered'>Scattered</Option>
            </Select>
          </RightSection>
        </TwoColumnRow>

        <InputGroup>
          <CheckboxWrapper>
            <Checkbox
              checked={editedG1N5.level2InspectionRequired}
              onChange={(e) =>
                updateG1N5Data('level2InspectionRequired', e.target.checked)
              }
            />
            <InputHeader style={{ margin: 0 }}>
              Level 2 Structural Inspection Required
            </InputHeader>
          </CheckboxWrapper>
        </InputGroup>

        <TwoColumnRow>
          <LeftSection>
            <InputHeader># Pictures Taken</InputHeader>
            <InputNumber
              min={0}
              value={editedG1N5.picturesTaken}
              onChange={(value) => updateG1N5Data('picturesTaken', value)}
              style={{ width: '100%' }}
              placeholder='Count'
            />
          </LeftSection>
          <RightSection>
            <InputHeader>Coating Condition</InputHeader>
            <Select
              value={editedG1N5.coatingCondition}
              onChange={(value) => updateG1N5Data('coatingCondition', value)}
              style={{ width: '100%' }}
              placeholder='Select condition'>
              <Option value='P1'>P1</Option>
              <Option value='P2'>P2</Option>
              <Option value='P3'>P3</Option>
              <Option value='P4'>P4</Option>
            </Select>
          </RightSection>
        </TwoColumnRow>

        <InputGroup>
          <InputHeader>Picture File Names</InputHeader>
          <Input
            value={editedG1N5.pictureFileNames}
            onChange={(e) => updateG1N5Data('pictureFileNames', e.target.value)}
            placeholder='Enter file names separated by commas'
          />
        </InputGroup>

        <InputGroup>
          <InputHeader>Comments</InputHeader>
          <TextArea
            value={editedG1N5.comments}
            onChange={(e) => updateG1N5Data('comments', e.target.value)}
            placeholder='Enter comments'
            rows={4}
          />
        </InputGroup>

        <Flex gap='small' style={{ width: '100%' }}>
          <Button block onClick={onClose}>
            Cancel
          </Button>
          <Button block onClick={handleSave}>
            Save
          </Button>
          {!exporting && (
            <Button block onClick={handleExport}>
              Export
            </Button>
          )}
          {exporting && <Button block>Exporting...</Button>}
        </Flex>
      </ModalContainer>
    </ModalBackdrop>
  )
}
